/**
 * Shopify CRM Panel
 * Shows customer purchase data from Shopify stores
 * Features: Filters, Export, Bulk Operations
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import type { PluginPanelProps } from '../_infrastructure/pluginTypes';
import { env } from '@/lib/env';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import type { ShopifyCustomer, ShopifyStats, Filters, ImportStep, ParsedSession } from './types';
import { DEFAULT_FILTERS } from './constants';
import { getAuthHeaders } from './lib/auth';
import { formatCurrency } from './utils/format';
import { exportToCSV } from './utils/csv';
import './ShopifyCRMPanel.css';

export function ShopifyCRMPanel(props: PluginPanelProps) {
  const { conversationId, instanceId, config, onOpenConfig } = props;
  const scopeConversationId = conversationId || '';
  const [stats, setStats] = useState<ShopifyStats | null>(null);
  const [allCustomers, setAllCustomers] = useState<ShopifyCustomer[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCustomer, setSelectedCustomer] = useState<ShopifyCustomer | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [productQuery, setProductQuery] = useState('');

  // Filter state
  const [filters, setFilters] = useState<Filters>(DEFAULT_FILTERS);
  const [showFilters, setShowFilters] = useState(false);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Import state
  const [importStep, setImportStep] = useState<ImportStep>('idle');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [storeName, setStoreName] = useState('');
  const [parsedSession, setParsedSession] = useState<ParsedSession | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<{ created: number; updated: number } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Clear data state
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  // Add-to-conversation state
  const [availableConversations, setAvailableConversations] = useState<Array<{ id: string; name: string }>>([]);
  const [showAddToCRM, setShowAddToCRM] = useState(false);
  const [customersToAdd, setCustomersToAdd] = useState<ShopifyCustomer[]>([]);
  const [selectedConversationTargetId, setSelectedConversationTargetId] = useState('');
  const [isAddingToCRM, setIsAddingToCRM] = useState(false);
  const [addToCRMResult, setAddToCRMResult] = useState<{ added: number; failed: number } | null>(null);

  // Fetch data — always workspace-wide (Shopify data spans all conversations)
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${env.apiBaseUrl}/contacts?limit=10000`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        setStats({ totalCustomers: 0, totalRevenue: 0, totalGross: 0, totalRefunded: 0, totalOrders: 0, avgOrderValue: 0, storeBreakdown: [] });
        return;
      }

      const data = await response.json();
      const contacts = data.items || [];

      const shopifyCustomers: ShopifyCustomer[] = contacts
        .filter((c: any) => {
          const orders = c.shopifyOrders || c.attributes?.shopifyOrders;
          return orders && orders.length > 0;
        })
        .map((c: any) => ({
          id: c.id,
          name: c.title || c.name || c.attributes?.name || c.email || c.attributes?.email || 'Unknown',
          email: c.email || c.attributes?.email || '',
          phone: c.phone || c.attributes?.phone || '',
          company: c.company || c.attributes?.company || '',
          address: c.address || c.attributes?.address || '',
          city: c.city || c.attributes?.city || '',
          state: c.state || c.attributes?.state || '',
          zip: c.zip || c.attributes?.zip || '',
          country: c.country || c.attributes?.country || '',
          totalSpent: c.shopifyTotalSpent || c.attributes?.shopifyTotalSpent || 0,
          orderCount: c.shopifyOrderCount || c.attributes?.shopifyOrderCount || (c.shopifyOrders || c.attributes?.shopifyOrders)?.length || 0,
          stores: c.shopifyStores || c.attributes?.shopifyStores || [],
          orders: c.shopifyOrders || c.attributes?.shopifyOrders || [],
          firstOrderDate: c.shopifyFirstOrderDate || c.attributes?.shopifyFirstOrderDate,
          lastOrderDate: c.shopifyLastOrderDate || c.attributes?.shopifyLastOrderDate,
        }));

      let totalGross = 0;
      let totalRefunded = 0;
      for (const c of contacts) {
        totalGross += c.shopifyTotalGross || c.attributes?.shopifyTotalGross || 0;
        totalRefunded += c.shopifyTotalRefunded || c.attributes?.shopifyTotalRefunded || 0;
      }

      const totalRevenue = shopifyCustomers.reduce((sum, c) => sum + c.totalSpent, 0);
      const totalOrders = shopifyCustomers.reduce((sum, c) => sum + c.orderCount, 0);

      const storeMap = new Map<string, { customers: Set<string>; revenue: number }>();
      shopifyCustomers.forEach((c) => {
        c.stores.forEach((store) => {
          if (!storeMap.has(store)) storeMap.set(store, { customers: new Set(), revenue: 0 });
          const entry = storeMap.get(store)!;
          entry.customers.add(c.id);
          entry.revenue += c.totalSpent / c.stores.length;
        });
      });

      const storeBreakdown = Array.from(storeMap.entries()).map(([store, data]) => ({
        store,
        customers: data.customers.size,
        revenue: data.revenue,
      }));

      setStats({
        totalCustomers: shopifyCustomers.length,
        totalRevenue,
        totalGross,
        totalRefunded,
        totalOrders,
        avgOrderValue: totalOrders > 0 ? totalRevenue / totalOrders : 0,
        storeBreakdown,
      });

      const sorted = [...shopifyCustomers].sort((a, b) => b.totalSpent - a.totalSpent);
      setAllCustomers(sorted);
    } catch (err) {
      setStats({ totalCustomers: 0, totalRevenue: 0, totalGross: 0, totalRefunded: 0, totalOrders: 0, avgOrderValue: 0, storeBreakdown: [] });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Get unique stores
  const existingStores = stats?.storeBreakdown.map(s => s.store) || [];

  // Apply filters to customers
  const filteredCustomers = useMemo(() => {
    let result = allCustomers;

    // Text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(query) ||
        c.email.toLowerCase().includes(query) ||
        (c.city && c.city.toLowerCase().includes(query)) ||
        (c.state && c.state.toLowerCase().includes(query)) ||
        (c.country && c.country.toLowerCase().includes(query))
      );
    }

    // Product search
    if (productQuery.trim()) {
      const query = productQuery.toLowerCase();
      result = result.filter(c =>
        c.orders.some(order =>
          order.items.some(item => item.toLowerCase().includes(query))
        )
      );
    }

    // Spend filters
    if (filters.minSpend !== null) {
      result = result.filter(c => c.totalSpent >= filters.minSpend!);
    }
    if (filters.maxSpend !== null) {
      result = result.filter(c => c.totalSpent <= filters.maxSpend!);
    }

    // Order count filters
    if (filters.minOrders !== null) {
      result = result.filter(c => c.orderCount >= filters.minOrders!);
    }
    if (filters.maxOrders !== null) {
      result = result.filter(c => c.orderCount <= filters.maxOrders!);
    }

    // Store filter
    if (filters.store) {
      if (filters.store === '__both__') {
        // Customers who bought from multiple stores
        result = result.filter(c => c.stores.length > 1);
      } else {
        result = result.filter(c => c.stores.includes(filters.store));
      }
    }

    // Location filter
    if (filters.location.trim()) {
      const loc = filters.location.toLowerCase();
      result = result.filter(c =>
        (c.city && c.city.toLowerCase().includes(loc)) ||
        (c.state && c.state.toLowerCase().includes(loc)) ||
        (c.country && c.country.toLowerCase().includes(loc))
      );
    }

    // Last order date filter
    if (filters.lastOrderDays !== null) {
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - filters.lastOrderDays);
      result = result.filter(c => {
        if (!c.lastOrderDate) return false;
        return new Date(c.lastOrderDate) >= cutoffDate;
      });
    }

    return result;
  }, [allCustomers, searchQuery, productQuery, filters]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return filters.minSpend !== null ||
      filters.maxSpend !== null ||
      filters.minOrders !== null ||
      filters.maxOrders !== null ||
      filters.store !== '' ||
      filters.location.trim() !== '' ||
      filters.lastOrderDays !== null;
  }, [filters]);

  // Top customers (from filtered list)
  const topCustomers = filteredCustomers.slice(0, 5);

  // Bulk selection helpers
  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(filteredCustomers.map(c => c.id)));
  };

  const selectNone = () => {
    setSelectedIds(new Set());
  };

  const selectedCustomers = filteredCustomers.filter(c => selectedIds.has(c.id));

  // Import handlers - use dedicated plugin endpoint
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file is CSV
    if (!file.name.endsWith('.csv')) {
      setImportError('Please select a CSV file');
      setImportStep('error');
      return;
    }

    setSelectedFile(file);
    setImportStep('parsed');
    setImportError(null);
  };

  const handleImport = async () => {
    if (!selectedFile || !storeName.trim()) return;

    setImportStep('importing');
    setImportError(null);

    try {
      // Use dedicated plugin import endpoint (single request)
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('storeName', storeName.trim());

      const response = await fetch(`${env.apiBaseUrl}/workspaces/${getActiveWorkspaceId()}/plugins/shopify-crm/import`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData,
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Import failed' }));
        throw new Error(err.error || err.details || 'Import failed');
      }

      const data = await response.json();
      setImportResult({
        created: data.summary?.created || 0,
        updated: data.summary?.updated || 0,
      });
      setImportStep('done');

      setTimeout(() => {
        fetchData();
        setTimeout(() => {
          setImportStep('idle');
          setSelectedFile(null);
          setStoreName('');
          setParsedSession(null);
          setImportResult(null);
        }, 3000);
      }, 500);
    } catch (err) {
      setImportError((err as Error).message);
      setImportStep('error');
    }
  };

  const resetImport = () => {
    setImportStep('idle');
    setSelectedFile(null);
    setStoreName('');
    setParsedSession(null);
    setImportError(null);
    setImportResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClearData = async () => {
    if (!stats || stats.totalCustomers === 0) return;

    setIsClearing(true);
    try {
      const response = await fetch(`${env.apiBaseUrl}/contacts?limit=10000`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) throw new Error('Failed to fetch contacts');

      const data = await response.json();
      const contacts = data.items || [];

      for (const contact of contacts) {
        const hasShopify = contact.shopifyOrders || contact.attributes?.shopifyOrders;
        if (!hasShopify) continue;

        await fetch(`${env.apiBaseUrl}/contacts/${contact.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            shopifyOrders: null,
            shopifyTotalSpent: null,
            shopifyTotalGross: null,
            shopifyTotalRefunded: null,
            shopifyOrderCount: null,
            shopifyFirstOrderDate: null,
            shopifyLastOrderDate: null,
            shopifyStores: null,
          }),
        });
      }

      setShowClearConfirm(false);
      fetchData();
    } catch (err) {
      console.error('Failed to clear Shopify data:', err);
    } finally {
      setIsClearing(false);
    }
  };

  // Conversation helpers for Add to CRM
  const fetchConversations = async () => {
    const workspaceId = getActiveWorkspaceId();
    if (!workspaceId) return;
    try {
      const response = await fetch(
        `${env.apiBaseUrl}/workspaces/${encodeURIComponent(workspaceId)}/conversations`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) return;
      const data = await response.json();
      const conversations = (data.items || data.conversations || [])
        .filter((item: any) => item.id !== scopeConversationId)
        .map((item: any) => ({
          id: item.id,
          name: item.title || item.name || 'Untitled conversation',
        }));
      setAvailableConversations(conversations);
    } catch (err) {
      console.error('Failed to fetch conversations:', err);
    }
  };

  const openAddToCRM = (customers: ShopifyCustomer[]) => {
    setCustomersToAdd(customers);
    setAddToCRMResult(null);
    setSelectedConversationTargetId('');
    fetchConversations();
    setShowAddToCRM(true);
  };

  const handleAddToCRM = async () => {
    if (!selectedConversationTargetId || customersToAdd.length === 0) return;
    const workspaceId = getActiveWorkspaceId();
    if (!workspaceId) return;

    setIsAddingToCRM(true);
    let added = 0;
    let failed = 0;

    const conversationLinksBaseUrl =
      `${env.apiBaseUrl}/workspaces/${encodeURIComponent(workspaceId)}` +
      `/conversations/${encodeURIComponent(selectedConversationTargetId)}/links`;
    const linkedObjectIds = new Set<string>();

    try {
      const existingLinksResponse = await fetch(
        `${conversationLinksBaseUrl}?object_type=contact&limit=10000`,
        { headers: getAuthHeaders() }
      );
      if (existingLinksResponse.ok) {
        const existingLinksData = await existingLinksResponse.json();
        const existingLinks = Array.isArray(existingLinksData.items) ? existingLinksData.items : [];
        for (const link of existingLinks) {
          const objectId = typeof link.objectId === 'string'
            ? link.objectId
            : typeof link.object_id === 'string'
              ? link.object_id
              : '';
          if (objectId) {
            linkedObjectIds.add(objectId);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to preload existing conversation links for Shopify import', { error });
    }

    for (const customer of customersToAdd) {
      try {
        if (linkedObjectIds.has(customer.id)) {
          added++;
          continue;
        }

        const linkResponse = await fetch(conversationLinksBaseUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeaders(),
          },
          body: JSON.stringify({
            objectId: customer.id,
            objectType: 'contact',
          }),
        });

        if (linkResponse.ok || linkResponse.status === 409) {
          linkedObjectIds.add(customer.id);
          added++;
        } else {
          const errorText = await linkResponse.text();
          console.error(
            `Failed to add contact ${customer.id} to conversation:`,
            linkResponse.status,
            errorText
          );
          failed++;
        }
      } catch (err) {
        console.error(`Error adding contact ${customer.id} to conversation:`, err);
        failed++;
      }
    }

    setAddToCRMResult({ added, failed });
    setIsAddingToCRM(false);
  };

  const closeAddToCRM = () => {
    setShowAddToCRM(false);
    setCustomersToAdd([]);
    setSelectedConversationTargetId('');
    setAddToCRMResult(null);
  };

  // Reset filters
  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setSearchQuery('');
    setProductQuery('');
  };

  if (loading) {
    return <div className="shopify-panel-loading">Loading Shopify data...</div>;
  }

  // Empty state
  if (!stats || stats.totalCustomers === 0) {
    return (
      <div className="shopify-panel xv-scroll">
        <div className="shopify-empty">
          <div className="shopify-empty-icon">🛒</div>
          <p className="shopify-empty-title">Import Your Shopify Orders</p>
          <p className="shopify-empty-subtitle">Track customer purchases across your stores</p>
        </div>

        <div className="shopify-import-box">
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />

          {importStep === 'idle' && (
            <>
              <button onClick={() => fileInputRef.current?.click()} className="shopify-import-btn">
                Select Shopify CSV File
              </button>
              <p className="shopify-import-hint">Export from Shopify Admin → Orders → Export</p>
            </>
          )}

          {importStep === 'parsed' && selectedFile && (
            <div className="shopify-import-form">
              <div className="shopify-import-info">
                Ready to import <strong>{selectedFile.name}</strong>
              </div>
              <div className="shopify-input-group">
                <label>Store Name</label>
                <input
                  type="text"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  placeholder="e.g., My Shopify Store"
                  className="shopify-input"
                />
              </div>
              <div className="shopify-import-actions">
                <button onClick={resetImport} className="shopify-btn-secondary">Cancel</button>
                <button onClick={handleImport} disabled={!storeName.trim()} className="shopify-btn-primary">Import</button>
              </div>
            </div>
          )}

          {importStep === 'importing' && <div className="shopify-import-status">Importing customers...</div>}

          {importStep === 'done' && importResult && (
            <div className="shopify-import-success">Done! Created {importResult.created}, updated {importResult.updated} contacts.</div>
          )}

          {importStep === 'error' && (
            <>
              <div className="shopify-import-error">{importError}</div>
              <button onClick={resetImport} className="shopify-btn-secondary">Try Again</button>
            </>
          )}
        </div>

        <p className="shopify-tip"><strong>Tip:</strong> Import from multiple stores to see which customers bought from both!</p>
      </div>
    );
  }

  return (
    <div className="shopify-panel xv-scroll">
      {/* Stats Grid */}
      <div className="shopify-stats">
        <div className="shopify-stat customers">
          <div className="shopify-stat-label">Customers</div>
          <div className="shopify-stat-value">{stats.totalCustomers.toLocaleString()}</div>
        </div>
        <div className="shopify-stat revenue">
          <div className="shopify-stat-label">Net Revenue</div>
          <div className="shopify-stat-value">{formatCurrency(stats.totalRevenue)}</div>
          {stats.totalRefunded > 0 && (
            <div className="shopify-stat-note">({formatCurrency(stats.totalRefunded)} refunded)</div>
          )}
        </div>
        <div className="shopify-stat orders">
          <div className="shopify-stat-label">Orders</div>
          <div className="shopify-stat-value">{stats.totalOrders.toLocaleString()}</div>
        </div>
        <div className="shopify-stat average">
          <div className="shopify-stat-label">Avg Order</div>
          <div className="shopify-stat-value">{formatCurrency(stats.avgOrderValue)}</div>
        </div>
      </div>

      {/* Store Breakdown */}
      {stats.storeBreakdown.length > 0 && (
        <div className="shopify-stores">
          <div className="shopify-stores-title">By Store:</div>
          {stats.storeBreakdown.map((s) => (
            <div key={s.store} className="shopify-store-row">
              <span>{s.store}</span>
              <span>{s.customers} customers • {formatCurrency(s.revenue)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Search & Filters Section */}
      <div className="shopify-search-filters">
        <div className="shopify-search-row">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search name, email, location..."
            className="shopify-search-input"
          />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`shopify-filter-toggle ${showFilters || hasActiveFilters ? 'active' : ''}`}
            title="Filters"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            {hasActiveFilters && <span className="shopify-filter-badge" />}
          </button>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="shopify-filters-panel">
            <div className="shopify-filters-grid">
              {/* Spend Filter */}
              <div className="shopify-filter-group">
                <label>Total Spent</label>
                <div className="shopify-filter-row">
                  <input
                    type="number"
                    placeholder="Min $"
                    value={filters.minSpend ?? ''}
                    onChange={(e) => setFilters(f => ({ ...f, minSpend: e.target.value ? Number(e.target.value) : null }))}
                    className="shopify-filter-input"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    placeholder="Max $"
                    value={filters.maxSpend ?? ''}
                    onChange={(e) => setFilters(f => ({ ...f, maxSpend: e.target.value ? Number(e.target.value) : null }))}
                    className="shopify-filter-input"
                  />
                </div>
                <div className="shopify-quick-filters">
                  <button onClick={() => setFilters(f => ({ ...f, minSpend: 100, maxSpend: null }))}>$100+</button>
                  <button onClick={() => setFilters(f => ({ ...f, minSpend: 500, maxSpend: null }))}>$500+</button>
                  <button onClick={() => setFilters(f => ({ ...f, minSpend: 1000, maxSpend: null }))}>$1k+</button>
                </div>
              </div>

              {/* Orders Filter */}
              <div className="shopify-filter-group">
                <label>Order Count</label>
                <div className="shopify-filter-row">
                  <input
                    type="number"
                    placeholder="Min"
                    value={filters.minOrders ?? ''}
                    onChange={(e) => setFilters(f => ({ ...f, minOrders: e.target.value ? Number(e.target.value) : null }))}
                    className="shopify-filter-input"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    placeholder="Max"
                    value={filters.maxOrders ?? ''}
                    onChange={(e) => setFilters(f => ({ ...f, maxOrders: e.target.value ? Number(e.target.value) : null }))}
                    className="shopify-filter-input"
                  />
                </div>
                <div className="shopify-quick-filters">
                  <button onClick={() => setFilters(f => ({ ...f, minOrders: 1, maxOrders: 1 }))}>1 order</button>
                  <button onClick={() => setFilters(f => ({ ...f, minOrders: 2, maxOrders: null }))}>2+</button>
                  <button onClick={() => setFilters(f => ({ ...f, minOrders: 5, maxOrders: null }))}>5+</button>
                </div>
              </div>

              {/* Store Filter */}
              <div className="shopify-filter-group">
                <label>Store</label>
                <select
                  value={filters.store}
                  onChange={(e) => setFilters(f => ({ ...f, store: e.target.value }))}
                  className="shopify-filter-select"
                >
                  <option value="">All Stores</option>
                  {existingStores.length > 1 && <option value="__both__">Both Stores (VIPs)</option>}
                  {existingStores.map(store => (
                    <option key={store} value={store}>{store}</option>
                  ))}
                </select>
              </div>

              {/* Location Filter */}
              <div className="shopify-filter-group">
                <label>Location</label>
                <input
                  type="text"
                  placeholder="City, state, or country"
                  value={filters.location}
                  onChange={(e) => setFilters(f => ({ ...f, location: e.target.value }))}
                  className="shopify-filter-input full"
                />
              </div>

              {/* Last Order Filter */}
              <div className="shopify-filter-group">
                <label>Last Order</label>
                <div className="shopify-quick-filters">
                  <button
                    className={filters.lastOrderDays === 30 ? 'active' : ''}
                    onClick={() => setFilters(f => ({ ...f, lastOrderDays: f.lastOrderDays === 30 ? null : 30 }))}
                  >
                    30 days
                  </button>
                  <button
                    className={filters.lastOrderDays === 90 ? 'active' : ''}
                    onClick={() => setFilters(f => ({ ...f, lastOrderDays: f.lastOrderDays === 90 ? null : 90 }))}
                  >
                    90 days
                  </button>
                  <button
                    className={filters.lastOrderDays === 365 ? 'active' : ''}
                    onClick={() => setFilters(f => ({ ...f, lastOrderDays: f.lastOrderDays === 365 ? null : 365 }))}
                  >
                    1 year
                  </button>
                </div>
              </div>

              {/* Product Search */}
              <div className="shopify-filter-group">
                <label>Product Purchased</label>
                <input
                  type="text"
                  placeholder="e.g., Bitcoin Book"
                  value={productQuery}
                  onChange={(e) => setProductQuery(e.target.value)}
                  className="shopify-filter-input full"
                />
              </div>
            </div>

            {hasActiveFilters && (
              <button onClick={resetFilters} className="shopify-reset-filters">
                Clear All Filters
              </button>
            )}
          </div>
        )}

        {/* Results Summary & Actions */}
        <div className="shopify-results-bar">
          <div className="shopify-results-count">
            {filteredCustomers.length === allCustomers.length
              ? `${allCustomers.length} customers`
              : `${filteredCustomers.length} of ${allCustomers.length} customers`}
          </div>
          <div className="shopify-results-actions">
            <button onClick={() => exportToCSV(filteredCustomers)} className="shopify-action-btn" title="Export to CSV">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Bulk Selection Bar */}
      {selectedIds.size > 0 && (
        <div className="shopify-bulk-bar">
          <span className="shopify-bulk-count">{selectedIds.size} selected</span>
          <div className="shopify-bulk-actions">
            <button onClick={() => exportToCSV(selectedCustomers)}>Export Selected</button>
            <button onClick={() => openAddToCRM(selectedCustomers)}>Add to Conversation</button>
            <button onClick={selectNone} className="shopify-bulk-clear">Clear</button>
          </div>
        </div>
      )}

      {/* Customer List */}
      <div className="shopify-customer-list">
        <div className="shopify-list-header">
          <label className="shopify-checkbox-label">
            <input
              type="checkbox"
              checked={selectedIds.size === filteredCustomers.length && filteredCustomers.length > 0}
              onChange={(e) => e.target.checked ? selectAll() : selectNone()}
            />
            <span className="shopify-select-all-text">
              {selectedIds.size > 0 ? `${selectedIds.size} selected` : 'Select all'}
            </span>
          </label>
        </div>

        <div className="shopify-results xv-scroll">
          {filteredCustomers.slice(0, 50).map((customer) => (
            <div
              key={customer.id}
              className={`shopify-result-item ${selectedIds.has(customer.id) ? 'selected' : ''}`}
            >
              <label className="shopify-checkbox-cell" onClick={(e) => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedIds.has(customer.id)}
                  onChange={() => toggleSelection(customer.id)}
                />
              </label>
              <div className="shopify-result-content" onClick={() => setSelectedCustomer(customer)}>
                <div className="shopify-result-row">
                  <span className="shopify-result-name">{customer.name}</span>
                  <span className="shopify-result-amount">{formatCurrency(customer.totalSpent)}</span>
                </div>
                <div className="shopify-result-meta">
                  {customer.email} • {customer.orderCount} order{customer.orderCount !== 1 ? 's' : ''}
                  {customer.stores.length > 1 && ' • ⭐ Multi-store'}
                  {customer.city && ` • ${customer.city}${customer.state ? `, ${customer.state}` : ''}`}
                </div>
              </div>
            </div>
          ))}

          {filteredCustomers.length > 50 && (
            <div className="shopify-more-results">
              Showing 50 of {filteredCustomers.length} • Use filters to narrow down
            </div>
          )}

          {filteredCustomers.length === 0 && (
            <div className="shopify-no-results">
              No customers match your filters
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="shopify-footer">
        <div className="shopify-footer-tip">
          Ask in chat: "Has [name] bought from us?"
        </div>
        <div className="shopify-footer-actions">
          <input ref={fileInputRef} type="file" accept=".csv" onChange={handleFileSelect} style={{ display: 'none' }} />
          <button
            onClick={() => { resetImport(); setTimeout(() => fileInputRef.current?.click(), 50); }}
            className="shopify-footer-btn"
          >
            + Import More
          </button>
          <button onClick={() => setShowClearConfirm(true)} className="shopify-footer-btn shopify-footer-btn-danger">
            Clear Data
          </button>
        </div>
      </div>

      {/* Clear Confirmation Modal */}
      {showClearConfirm && (
        <div className="shopify-modal-overlay">
          <div className="shopify-modal">
            <div className="shopify-modal-title">Clear Shopify Data?</div>
            <p className="shopify-modal-text">
              This will remove purchase history from {stats.totalCustomers} contacts. The contacts themselves will remain.
            </p>
            <div className="shopify-modal-actions">
              <button onClick={() => setShowClearConfirm(false)} className="shopify-btn-secondary" disabled={isClearing}>Cancel</button>
              <button onClick={handleClearData} className="shopify-btn-danger" disabled={isClearing}>
                {isClearing ? 'Clearing...' : 'Clear Data'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Conversation Modal */}
      {showAddToCRM && (
        <div className="shopify-modal-overlay" onClick={closeAddToCRM}>
          <div className="shopify-modal shopify-add-to-conversation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="shopify-modal-title">
              Add {customersToAdd.length} Customer{customersToAdd.length !== 1 ? 's' : ''} to Conversation
            </div>

            {!addToCRMResult ? (
              <>
                <p className="shopify-modal-text">Select a conversation to add these contacts to:</p>
                {availableConversations.length === 0 ? (
                  <p className="shopify-modal-text">Loading conversations...</p>
                ) : (
                  <select
                    value={selectedConversationTargetId}
                    onChange={(e) => setSelectedConversationTargetId(e.target.value)}
                    className="shopify-conversation-select"
                  >
                    <option value="">Select a conversation...</option>
                    {availableConversations.map((conversation) => (
                      <option key={conversation.id} value={conversation.id}>{conversation.name}</option>
                    ))}
                  </select>
                )}
                <div className="shopify-modal-actions">
                  <button onClick={closeAddToCRM} className="shopify-btn-secondary" disabled={isAddingToCRM}>Cancel</button>
                  <button
                    onClick={handleAddToCRM}
                    className="shopify-btn-primary"
                    disabled={!selectedConversationTargetId || isAddingToCRM}
                  >
                    {isAddingToCRM ? 'Adding...' : 'Add to Conversation'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="shopify-add-result">
                  <div className="shopify-add-result-success">
                    {addToCRMResult.added} contact{addToCRMResult.added !== 1 ? 's' : ''} added to conversation
                  </div>
                  {addToCRMResult.failed > 0 && <div className="shopify-add-result-failed">{addToCRMResult.failed} failed</div>}
                </div>
                <div className="shopify-modal-actions">
                  <button onClick={closeAddToCRM} className="shopify-btn-primary">Done</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Customer Detail Modal */}
      {selectedCustomer && (
        <div className="shopify-modal-overlay" onClick={() => setSelectedCustomer(null)}>
          <div className="shopify-customer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="shopify-customer-modal-header">
              <div className="shopify-customer-modal-title">{selectedCustomer.name}</div>
              <button className="shopify-modal-close" onClick={() => setSelectedCustomer(null)}>×</button>
            </div>

            <div className="shopify-customer-modal-body xv-scroll">
              <div className="shopify-customer-contact">
                {selectedCustomer.email && (
                  <div className="shopify-customer-field">
                    <span className="shopify-field-label">Email</span>
                    <a href={`mailto:${selectedCustomer.email}`} className="shopify-field-value shopify-link">{selectedCustomer.email}</a>
                  </div>
                )}
                {selectedCustomer.phone && (
                  <div className="shopify-customer-field">
                    <span className="shopify-field-label">Phone</span>
                    <a href={`tel:${selectedCustomer.phone}`} className="shopify-field-value shopify-link">{selectedCustomer.phone}</a>
                  </div>
                )}
                {selectedCustomer.company && (
                  <div className="shopify-customer-field">
                    <span className="shopify-field-label">Company</span>
                    <span className="shopify-field-value">{selectedCustomer.company}</span>
                  </div>
                )}
                {(selectedCustomer.city || selectedCustomer.state || selectedCustomer.country) && (
                  <div className="shopify-customer-field">
                    <span className="shopify-field-label">Location</span>
                    <span className="shopify-field-value">
                      {[selectedCustomer.city, selectedCustomer.state, selectedCustomer.country].filter(Boolean).join(', ')}
                    </span>
                  </div>
                )}
              </div>

              <div className="shopify-customer-stats">
                <div className="shopify-customer-stat">
                  <span className="shopify-stat-value">{formatCurrency(selectedCustomer.totalSpent)}</span>
                  <span className="shopify-stat-label">Total Spent</span>
                </div>
                <div className="shopify-customer-stat">
                  <span className="shopify-stat-value">{selectedCustomer.orderCount}</span>
                  <span className="shopify-stat-label">Orders</span>
                </div>
                <div className="shopify-customer-stat">
                  <span className="shopify-stat-value">{selectedCustomer.stores.join(', ')}</span>
                  <span className="shopify-stat-label">Store{selectedCustomer.stores.length > 1 ? 's' : ''}</span>
                </div>
              </div>

              <div className="shopify-order-history">
                <div className="shopify-order-history-title">Order History</div>
                {selectedCustomer.orders.map((order, idx) => (
                  <div key={idx} className="shopify-order">
                    <div className="shopify-order-header">
                      <span className="shopify-order-number">{order.orderNumber}</span>
                      <span className="shopify-order-amount">
                        {formatCurrency(order.netTotal || order.total)}
                        {order.refunded > 0 && <span className="shopify-order-refund"> (-{formatCurrency(order.refunded)})</span>}
                      </span>
                    </div>
                    <div className="shopify-order-meta">
                      {order.date && new Date(order.date).toLocaleDateString()} • {order.status}
                    </div>
                    {order.items.length > 0 && (
                      <div className="shopify-order-items">
                        {order.items.map((item, i) => <div key={i} className="shopify-order-item">• {item}</div>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="shopify-customer-modal-footer">
              <button onClick={() => setSelectedCustomer(null)} className="shopify-btn-secondary">Close</button>
              <button
                onClick={() => { openAddToCRM([selectedCustomer]); setSelectedCustomer(null); }}
                className="shopify-btn-primary"
              >
                Add to Conversation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
